import { useCallback, useEffect, useReducer } from 'react';

import { DateBoundaryType, DateFilterError, DateFilterType } from './date-filter.types';

type DateFiltersTypeWithErrors = {
  tempFilter: DateFilterType;
  appliedFilter: DateFilterType;
  errors: DateFilterError;
  flushTemp: boolean;
};

type DateFilterAction =
  | { action: 'reset' | 'resetTempFilter' | 'applyFilter' }
  | { action: 'updateTemp'; updatedFields: Partial<DateFilterType> }
  | { action: 'updateErrors'; updatedFields: Partial<DateFilterError> }
  | { action: 'setFilter'; filter: DateFilterType };

export const isDisabledDate = (
  date: Date,
  type: DateBoundaryType,
  startDate: Date | undefined,
  endDate: Date | undefined
): boolean => {
  if (type === 'start') {
    return endDate ? date > endDate : false;
  } else {
    return startDate ? date < startDate : false;
  }
};

export const isCompleteFilter = (filter: DateFilterType): boolean => {
  switch (filter.option) {
    case 'between': {
      return !!filter.startDate && !!filter.endDate;
    }
    case 'after': {
      return !!filter.startDate;
    }
    case 'before': {
      return !!filter.endDate;
    }
    case 'within last': {
      return !!filter.period && !!filter.periodQuantity;
    }
    case undefined: {
      return false;
    }
    default: {
      console.error('Unknown filter option while checking if the filter is complete: ' + filter.option);
      return false;
    }
  }
};

export const CLEARED_FILTER: DateFilterType = {
  option: undefined,
  startDate: undefined,
  endDate: undefined,
  period: 'days',
  periodQuantity: undefined,
};

export const CLEARED_FILTER_STATE: DateFiltersTypeWithErrors = {
  tempFilter: { ...CLEARED_FILTER },
  appliedFilter: { ...CLEARED_FILTER },
  errors: {},
  flushTemp: false,
};

export const dateFilterReducer = (
  filter: DateFiltersTypeWithErrors,
  action: DateFilterAction
): DateFiltersTypeWithErrors => {
  switch (action.action) {
    case 'reset': {
      return { ...CLEARED_FILTER_STATE };
    }
    case 'resetTempFilter': {
      return { ...filter, tempFilter: { ...CLEARED_FILTER } };
    }
    case 'updateTemp': {
      return {
        ...filter,
        tempFilter: { ...filter.tempFilter, ...action.updatedFields },
        flushTemp: true,
        errors: {},
      };
    }
    case 'updateErrors': {
      return {
        ...filter,
        errors: { ...filter.errors, ...action.updatedFields },
      };
    }
    case 'applyFilter': {
      return {
        ...filter,
        appliedFilter: { ...filter.tempFilter },
        flushTemp: false,
      };
    }
    case 'setFilter': {
      return {
        ...filter,
        appliedFilter: { ...action.filter },
        tempFilter: { ...action.filter },
      };
    }
  }
};

export const useDateFilterState = (
  onChange: (filter: DateFilterType) => void,
  onApply: (filter: DateFilterType) => void,
  value?: DateFilterType
) => {
  const [filter, dispatch] = useReducer(dateFilterReducer, { ...CLEARED_FILTER_STATE });

  useEffect(() => {
    if (value && isCompleteFilter(value)) {
      dispatch({ action: 'setFilter', filter: value });
    } else {
      dispatch({ action: 'reset' });
    }
  }, [value]);

  const reset = useCallback(() => {
    dispatch({ action: 'reset' });
    onChange?.({ ...CLEARED_FILTER });
    onApply?.({ ...CLEARED_FILTER });
  }, [onChange, onApply]);

  const resetTempFilter = () => {
    dispatch({ action: 'resetTempFilter' });
  };

  const apply = useCallback(() => {
    dispatch({ action: 'applyFilter' });
    onApply?.(filter.tempFilter);
    onChange?.(filter.tempFilter);
  }, [onApply, filter.tempFilter, onChange]);

  const updateTempFilter = (updatedFields: Partial<DateFilterType>) => {
    dispatch({ action: 'updateTemp', updatedFields: updatedFields });
  };

  const updateErrors = (updatedFields: Partial<DateFilterError>) => {
    dispatch({ action: 'updateErrors', updatedFields: updatedFields });
  };

  return { filter, reset, resetTempFilter, apply, updateTempFilter, updateErrors };
};
